'use client'

import React, { type ComponentPropsWithoutRef } from 'react'
import Link from 'next/link'

import { CodeSnippet } from '@graftcode/design-system'

import { MultiCodeSnippet } from '@common/components/MultiCodeSnippet/MultiCodeSnippet'
import { transformMultiCodeBlocks } from '@common/utils/transformMultiCodeBlocks'

import { slugify } from '../../ContentNav/utils/slugify'
import { MARKDOWN_LANGUAGE_LABELS } from '../markdown-renderer.const'
import type { MarkdownComponents } from '../MarkdownRenderer.types'

import { extractTextFromChildren } from './extractTextFromChildren'

import styles from '../MarkdownRenderer.module.scss'

type BuildMarkdownComponentsParams = {
  isClient: boolean
  onImageClick: (src: string, alt: string) => void
}

export function buildMarkdownComponents({ isClient, onImageClick }: BuildMarkdownComponentsParams): MarkdownComponents {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    code(props: any) {
      const { className, children, inline, ...restProps } = props
      const match = /language-(\w+)/.exec(className || '')
      const language: string = match?.[1] || 'text'
      const codeContent = String(children).replace(/\n$/, '')

      const isSingleLine = !codeContent.includes('\n')
      const isShortCode = codeContent.length < 100 && codeContent.split(/\s+/).length <= 5
      const hasNoLanguage = !className || className === 'language-text'

      if (inline || (isSingleLine && hasNoLanguage && isShortCode)) {
        return (
          <code className={styles.inlineCode} {...restProps}>
            {children}
          </code>
        )
      }

      const lang = language.toLowerCase()

      if (lang === 'multi' && isClient) {
        const { multiCodeBlocks } = transformMultiCodeBlocks(`\`\`\`multi\n${codeContent}\n\`\`\``)
        const variants = multiCodeBlocks.values().next().value
        if (variants && variants.length > 0) {
          return (
            <div className={styles.codeSnippetWrapper}>
              <MultiCodeSnippet variants={variants} />
            </div>
          )
        }
      }

      const isTerminal = ['bash', 'sh', 'shell', 'terminal', 'console', 'powershell', 'cmd', 'batch'].includes(lang)
      const labelText = isTerminal ? 'text' : (MARKDOWN_LANGUAGE_LABELS[lang] ?? language)

      if (isClient) {
        return (
          <div className={styles.codeSnippetWrapper}>
            <CodeSnippet
              code={codeContent}
              language={language}
              contentVariant={isTerminal ? 'terminal' : 'advanced'}
              labelText={labelText}
            />
          </div>
        )
      }

      return (
        <pre className={styles.codeBlockWrapper}>
          <code className={styles.codeBlock} {...restProps}>
            {children}
          </code>
        </pre>
      )
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    h1: (props: any) => <h1 className={styles.h1} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    h2: ({ children, ...props }: any) => {
      const text = extractTextFromChildren(children)
      return (
        <h2 id={slugify(text)} className={styles.h2} {...props}>
          {children}
        </h2>
      )
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    h3: ({ children, ...props }: any) => {
      const text = extractTextFromChildren(children)
      return (
        <h3 id={slugify(text)} className={styles.h3} {...props}>
          {children}
        </h3>
      )
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    h4: (props: any) => <h4 className={styles.h4} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    p: (props: any) => {
      const { children, ...restProps } = props
      // eslint-disable-next-line react-x/no-children-to-array -- Needed to detect and unwrap code blocks from paragraphs
      const childrenArray = React.Children.toArray(children)
      if (childrenArray.length === 1) {
        const child = childrenArray[0]
        if (React.isValidElement(child) && child.type === 'code') {
          const childProps = child.props as { className?: string }
          if (
            childProps.className &&
            typeof childProps.className === 'string' &&
            childProps.className.includes('language-')
          ) {
            return <>{child}</>
          }
        }
      }

      return (
        <p className={styles.paragraph} {...restProps}>
          {children}
        </p>
      )
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ul: (props: any) => <ul className={styles.list} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ol: (props: any) => <ol className={styles.orderedList} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    li: (props: any) => <li className={styles.listItem} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blockquote: (props: any) => <blockquote className={styles.blockquote} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    a: ({ href, ...props }: any) => {
      if (href?.startsWith('/')) {
        return <Link className={styles.link} href={href} {...props} />
      }
      return <a className={styles.link} target="_blank" rel="noopener noreferrer" href={href} {...props} />
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    table: (props: any) => (
      <div className={styles.tableWrapper}>
        <table className={styles.table} {...props} />
      </div>
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    thead: (props: any) => <thead className={styles.thead} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tbody: (props: any) => <tbody className={styles.tbody} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tr: (props: any) => <tr className={styles.tr} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    th: (props: any) => <th className={styles.th} {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    td: (props: any) => <td className={styles.td} {...props} />,
    img: ({ src, alt, ...props }: ComponentPropsWithoutRef<'img'>) => {
      const imageSrc = typeof src === 'string' ? src : ''
      const imageAlt = alt ?? ''
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageSrc}
          alt={imageAlt}
          className={styles.image}
          onClick={() => onImageClick(imageSrc, imageAlt)}
          {...props}
        />
      )
    },
    iframe: ({ src, title, height, ...props }: ComponentPropsWithoutRef<'iframe'>) => {
      const iframeSrc = typeof src === 'string' ? src : ''
      const iframeTitle = typeof title === 'string' && title.length > 0 ? title : 'Embedded content'
      const numericHeight =
        typeof height === 'number' ? height : typeof height === 'string' ? Number.parseInt(height, 10) : Number.NaN
      const resolvedHeight = Number.isFinite(numericHeight) ? `${numericHeight}px` : '26rem'

      return (
        <iframe
          src={iframeSrc}
          title={iframeTitle}
          className={styles.iframe}
          loading="lazy"
          style={{ height: resolvedHeight }}
          {...props}
        />
      )
    },
  }
}
